// src/components/ExperimentDashboard.js

import React from 'react';
import { StatusDisplay } from './StatusDisplay';
import { OrganoidChart } from './OrganoidChart';
import { SpikeHistogramChart } from './SpikeHistogramChart';
import { authFetch } from '../utils/authFetch';

export default function ExperimentDashboard({
  startExperiment,
  exitExperiment,
  currentUserId,
  uiState,
  status,
  phase,
  remainingSeconds,
  baselineProgress,
  dataBuffer,
  spikeBuffer,
  drugTimestamp,
  bufferSize,
  experimentStartTime,
  neuronIds,
  applyDrug,
  stopExperiment,
  selectedOrganoid,
  setIsRecording,
  setUiState,
  autoApplyDrug,
  setAutoApplyDrug,
  finalRecoveryFailed,
  setFinalRecoveryFailed,
  protocolName,
}) {

  const [showStopConfirm, setShowStopConfirm] = React.useState(false); 
  const [wasManuallyStopped, setWasManuallyStopped] = React.useState(false);
  const [showRobotOverlay, setShowRobotOverlay] = React.useState(false);

  if (!['ready', 'running', 'complete'].includes(uiState)) return null;

  const latestEnv = dataBuffer[dataBuffer.length - 1] || {};
  const { temp, oxygen } = latestEnv;

  function capitalizeFirst(str) {
    return str?.charAt(0).toUpperCase() + str?.slice(1);
  }

  return (
    <div className="dashboard-wrapper">
        <div className="dashboard-container">
            <div className="control-panel">

                {(uiState === 'complete' || finalRecoveryFailed) && (
                    <div className="completion-message">
                        <h2>Great Job!!</h2>
                        {wasManuallyStopped && (
                            <p>The protocol will finish silently in the background.</p>
                        )}

                        {finalRecoveryFailed && (
                            <p>
                                ✅ Experiment completed successfully.<br />
                                📁 All data has been securely saved and backed up.<br />
                            </p>
                        )}

                        <p>Results {!wasManuallyStopped ? 'are now part of' : 'will be added to'} our data lake, powering smarter AI tools and business-critical decisions. </p>
                        <p><strong>Subscribe to unlock full platform capabilities and actionable insights.</strong></p>
                        <button 
                            className="exit-button" 
                            onClick={() => {
                                window.location.href = "https://www.lithopsbio.com/";
                            }}
                        >
                            Close Application
                        </button>
                    </div>
                    )}

                    {uiState !== 'complete' && (
                        <>

                            <h1>Brain-on-a-Chip<br />Dashboard</h1>
                            <p style={{ fontWeight: 'bold',  color: status === 'recording' ? 'red' : 'green' }}>
                                Status: {capitalizeFirst(status) ?? 'Ready'} ( {phase ? capitalizeFirst(phase) : '-'} )
                            </p>

                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                            type="checkbox"
                            checked={autoApplyDrug}
                            onChange={(e) => {
                                const checked = e.target.checked;
                                setAutoApplyDrug(checked);

                                // ✅ Send intent to backend
                                authFetch(`${process.env.REACT_APP_API_URL}/schedule_drug`, {
                                    method: 'POST',
                                    body: JSON.stringify({ organoid_id: selectedOrganoid, auto_apply: checked })
                                });
                            }}
                            />
                                    Autotomate Drug Application
                                </label>
                            </div>

                            <div className="control-buttons">
                                {uiState === 'ready' ? (
                                    <div className="button-row">
                                        <button
                                            onClick={() => {
                                                if (!currentUserId) {
                                                alert("User ID missing. Please log in or refresh the page.");
                                                return;
                                                }
                                                startExperiment(selectedOrganoid, protocolName);
                                            }}
                                        >
                                            Start Experiment
                                        </button>

                                        <button 
                                            onClick={applyDrug}
                                            disabled={phase !== 'drug_ready'}
                                            style={{ 
                                                opacity: phase === 'drug_ready' ? 1 : 0.5, 
                                                cursor: phase === 'drug_ready' ? 'pointer' : 'not-allowed' 
                                            }}
                                            title={
                                                phase === 'drug_ready'
                                                    ? 'Ready to apply drug'
                                                    : 'Button will activate once baseline criteria are met'
                                            }
                                        >
                                            Apply Drug
                                        </button>
                                    </div>
                                ) : (
                                    <div className="button-row">
                                        <button onClick={() => setShowStopConfirm(true)}>Stop Recording</button>
                                        <button 
                                            onClick={applyDrug}
                                            disabled={phase !== 'drug_ready'}
                                            style={{ opacity: phase === 'drug_ready' ? 1 : 0.5, cursor: phase === 'drug_ready' ? 'pointer' : 'not-allowed' }}
                                        >
                                            Apply Drug
                                        </button>
                                        {/* <button onClick={applyDrug} disabled={uiState === 'complete'}>Apply Drug</button> */}
                                    </div>
                                )}
                            </div>

                            <div className="baseline-progress-wrapper">
                                <p style={{ margin: 0, fontWeight: 'bold' }}>Baseline:</p>
                                <div className="phase-progress-bar">
                                    <div 
                                        className="phase-progress-fill" 
                                        style={{
                                            height: '100%',
                                            borderRadius: '6px',
                                            width: `${baselineProgress}%`,
                                            backgroundColor:
                                                baselineProgress === 100
                                                    ? 'green'
                                                    : baselineProgress > 33
                                                    ? 'orange'
                                                    : 'red',
                                            transition: 'width 0.5s ease-in-out'  
                                        }} 
                                    />
                                </div>
                            </div>

                        </>
                    )}


                    {/* Show temp and oxygen only on small screens 
                    <div className="env-readings">
                        <div className="env-value">Temp: {temp != null ? temp.toFixed(1) : '—'}°C</div>
                        <div className="env-value">O₂: {oxygen != null ? oxygen.toFixed(1) : '—'}%</div>
                    </div> */}

            </div>

            {/* ChartPanel */}
            <div className="chart-panel" style={{ position: 'relative' }}>
                {finalRecoveryFailed && (
                    <div className="chart-panel-overlay">
                        <div className="robot-icon">⚠️</div>
                        <div>
                            Your session timed out. <br />
                            Dashboard plots may be incomplete. <br />
                        </div>
                    </div>
                )}
                
                {showRobotOverlay && (
                    <div className="chart-panel-overlay">
                        <div className="robot-icon">🤖</div>
                        <div>
                            Don’t worry<br /> 
                            The robots are completing the protocol.<br />
                            Close the dashboard & relax.
                        </div>
                    </div>
                )}

                {/* Show temp and oxygen only on small screens */}
                <div className="env-readings">
                    <div className="env-value">Temp: {temp != null ? temp.toFixed(1) : '—'}°C</div>
                    <div className="env-value">O₂: {oxygen != null ? oxygen.toFixed(1) : '—'}%</div>
                </div>

                {/* OrganoidChart only on large screens */}
                <div className="organoid-chart">
                    <OrganoidChart
                        dataBuffer={dataBuffer}
                        drugTimestamp={drugTimestamp}
                        status={status}
                        bufferSize={bufferSize}
                        experimentStartTime={experimentStartTime}
                    />
                </div>

                <SpikeHistogramChart
                    spikeBuffer={spikeBuffer}
                    drugTimestamp={drugTimestamp}
                    bufferSize={bufferSize}
                    experimentStartTime={experimentStartTime}
                    neuronIds={neuronIds}
                />
            </div>

            {showStopConfirm && (
            <div className="modal-overlay">
                <div className="modal-content">
                    <h3>Confirm Termination</h3>
                    <p>This will terminate the experiment, release organoid resources, and end the demonstration.</p>
                    <div className="modal-buttons">
                        <button onClick={() => {
                            stopExperiment(); // shuts down backend
                            exitExperiment("manual_stop", phase, spikeBuffer?.length || 0);

                            setIsRecording(false);
                            // setUiState('init'); // or 'select' if you want to restart
                            setUiState('complete');
                            setWasManuallyStopped(true);
                            setShowRobotOverlay(true);
                            setShowStopConfirm(false);
                            }}>
                            Confirm Stop
                        </button>
                        <button onClick={() => setShowStopConfirm(false)}>
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
            )}

        </div>
        {/* Responsive styles */}
        <style>
            {`
                /* Chart layout */
                .chart-section {
                    /* width: 100%; */
                }

                .chart-bar {
                    height: 200px;
                    /* width: 100%; */
                }

                .dashboard-wrapper {
                    display: flex;
                    justify-content: center;
                    padding: 2rem;
                }

                .dashboard-container {
                    display: flex;
                    flex-direction: column;
                    /* padding: 2rem; */
                    max-width: 1200px;
                    width: 100%;
                }

                .control-panel, 
                .chart-panel {
                    display: flex;
                    flex-direction: column;
                    position: 'relative';
                }

                .chart-panel > * {
                    width: 100%;
                }

                .chart-panel-overlay {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%; /* ✅ full viewport width for small screens */
                    height: 100%;
                    background-color: rgba(0, 100, 0, 0.6);;
                    z-index: 10;
                    display: flex;
                    flex-direction: column; /* ✅ stack vertically */
                    align-items: center;
                    justify-content: center;
                    color: #fff;
                    font-size: 1.5rem;
                    font-weight: normal;
                    backdrop-filter: blur(4px);
                    border-radius: 8px;
                    text-align: center;
                    padding: 1rem;
                    box-sizing: border-box; /* ✅ prevent overflow from padding */
                }

                .robot-icon {
                    font-size: 2.5rem; /* ✅ larger emoji */
                    margin-bottom: 1rem;
                }

                .control-status {
                    margin-bottom: 1.5rem;
                }

                .control-buttons {
                    margin-bottom: 1rem;
                }

                .env-readings {
                    text-align: center;
                }

                .env-value {
                    font-size: 2rem;
                    font-weight: bold;
                }

                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: rgba(0,0,0,0.6);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    z-index: 999;
                }

                .modal-content {
                    background: white;
                    padding: 2rem;
                    border-radius: 8px;
                    max-width: 400px;
                    text-align: center;
                }

                .modal-buttons {
                    display: flex;
                    justify-content: space-around;
                    margin-top: 1.5rem;
                }

                .modal-buttons button {
                    padding: 0.5rem 1rem;
                }

                .button-row {
                    display: flex;
                    flex-direction: row;
                    justify-content: center;
                    gap: 1rem;
                }

                .button-row button {
                    flex: 1;
                    padding: 0.5rem;
                    font-size: 1rem;
                }

                .baseline-progress-wrapper { 
                    display: flex; 
                    align-items: center;
                    gap: 12px;
                    margin-bottom: 1rem;
                }

                .phase-progress-bar { 
                    flex-grow: 1;
                    background-color: #eee;
                    height: 12px;
                    border-radius: 6px;
                    overflow: hidden;
                    margin-top: 0;
                }

                .completion-message {
                    display: flex;
                    flex-direction: column;
                    /* gap: 1rem; */
                    padding: 2rem;
                    text-align: center;
                    background: #f5f5f5;
                    border-radius: 8px;
                    margin-bottom: 2rem;
                }

                .completion-message h2 {
                    font-size: 1.8rem;
                    margin-bottom: 1rem;
                }

                .completion-message p {
                    font-size: 1.2rem;
                    line-height: 1.6;
                }

                .exit-button {
                    margin-top: 2rem;
                    padding: 0.75rem 1.5rem;
                    font-size: 1rem;
                    font-weight: bold;
                    background-color: #333;
                    color: white;
                    border: none;
                    border-radius: 6px;
                    cursor: pointer;
                }

                .exit-button:hover {
                    background-color: #555;
                }

                @media (min-width: 768px) {

                    .dashboard-container {
                        display: flex;
                        flex-direction: row;
                        gap: 2rem
                    }

                    .chart-panel ,
                    .control-panel {
                        flex: 1;
                        min-width: 0; /* prevents overflow from child content */
                    }


                    .control-panel {
                        max-width: 300px;
                    }   

                    .chart-panel-overlay {
                        width: 100%; /* ✅ match chart-panel width */
                        border-radius: 8px;
                    }

                    .env-readings {
                        display: none;
                    }

                    .organoid-chart {
                        display: block;
                    }

                }

                @media (max-width: 767px) {

                    .env-readings {
                        display: block;
                        margin-bottom: 2rem;
                    }

                    .organoid-chart {
                        display: none;
                    }

                }

            `}
        </style>
    </div>
  );
}